import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useRepositories } from '../../hooks/useRepositories';
import {
  createRepository,
  deleteRepository,
  validateRepositoryExists,
} from '../../api/repositoriesHttpClient';
import ActionConfirmation from '../../../../Common/components/ActionConfirmation/ActionConfirmation';
import CreateRepositoryDialog from '../CreateRepositoryDialog/CreateRepositoryDialog';
import RepositoriesTable from '../RepositoriesTable/RepositoriesTable';

interface RepositoriesProps {
  projectId: string;
  toolId: string;
  toolProjectId: string;
}

const REPOSITORY_NAME_PATTERN = /^[a-zA-Z0-9._-]+$/;

const Repositories: React.FC<RepositoriesProps> = ({ projectId, toolId, toolProjectId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { repositories, loading, error, fetchRepositories } = useRepositories();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newRepositoryName, setNewRepositoryName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [repositoryNameExists, setRepositoryNameExists] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    repositoryName: string;
  }>({ show: false, repositoryName: '' });

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    fetchRepositories(projectId);
  }, [fetchRepositories, projectId, toolId, toolProjectId]);

  const validateRepositoryName = (name: string): string | null => {
    if (/\s/.test(name)) {
      return 'Repository name cannot contain whitespace';
    }
    if (name && !REPOSITORY_NAME_PATTERN.test(name)) {
      return 'Repository name can only contain letters, numbers, dots, hyphens and underscores';
    }
    return null;
  };

  const debouncedValidation = (name: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      checkRepositoryNameExists(name);
    }, 500);
  };

  const checkRepositoryNameExists = async (name: string) => {
    if (!name.trim()) {
      setRepositoryNameExists(false);
      return;
    }
    setIsValidating(true);
    try {
      const exists = await validateRepositoryExists(toolId, name.trim());
      setRepositoryNameExists(exists);
    } catch (err) {
      console.error('Error validating repository name:', err);
      setRepositoryNameExists(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleRepositoryNameChange = (name: string) => {
    setNewRepositoryName(name);
    setRepositoryNameExists(false);
    const validationError = validateRepositoryName(name);
    if (validationError) {
      setCreateError(validationError);
      return;
    }
    setCreateError(null);
    debouncedValidation(name);
  };

  const handleCreateRepository = async () => {
    if (!newRepositoryName.trim()) {
      setCreateError('Repository name is required');
      return;
    }
    const validationError = validateRepositoryName(newRepositoryName);
    if (validationError) {
      setCreateError(validationError);
      return;
    }
    if (repositoryNameExists) {
      setCreateError('A repository with this name already exists');
      return;
    }
    if (isValidating) {
      return;
    }

    // Cancel any pending debounced validation so it doesn't fire again after creation starts
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = undefined;
    }

    const repositoryNameToCreate = newRepositoryName.trim();

    setCreateError(null);
    setIsCreateDialogOpen(false);
    setNewRepositoryName('');
    setIsLoading(true);

    try {
      await createRepository({ projectId, toolId, toolProjectId, repositoryName: repositoryNameToCreate });
      enqueueSnackbar(`Repository "${repositoryNameToCreate}" has been created successfully!`, {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      await fetchRepositories(projectId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setCreateError(errorMessage);
      enqueueSnackbar(`Failed to create repository: ${errorMessage}`, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRepository = (repositoryName: string) => {
    setDeleteConfirmation({ show: true, repositoryName });
  };

  const confirmDeleteRepository = async () => {
    const deletedRepositoryName = deleteConfirmation.repositoryName;
    setDeleteConfirmation({ show: false, repositoryName: '' });
    setIsLoading(true);

    try {
      await deleteRepository({ projectId, toolId, toolProjectId, repositoryName: deletedRepositoryName });
      enqueueSnackbar(`Repository "${deletedRepositoryName}" has been deleted.`, {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      await fetchRepositories(projectId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      enqueueSnackbar(`Failed to delete repository: ${errorMessage}`, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      console.error('Failed to delete repository:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDeleteRepository = () => {
    setDeleteConfirmation({ show: false, repositoryName: '' });
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setNewRepositoryName('');
    setCreateError(null);
    setRepositoryNameExists(false);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography variant="h6">Repositories</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={isLoading}
        >
          Create Repository
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <RepositoriesTable
            repositories={repositories}
            isLoading={isLoading}
            onDeleteRepository={handleDeleteRepository}
          />
        )}
      </Box>

      <CreateRepositoryDialog
        open={isCreateDialogOpen}
        repositoryName={newRepositoryName}
        createError={createError}
        isLoading={isLoading}
        isValidating={isValidating}
        repositoryNameExists={repositoryNameExists}
        onClose={handleCloseCreateDialog}
        onRepositoryNameChange={handleRepositoryNameChange}
        onCreateRepository={handleCreateRepository}
      />

      <ActionConfirmation
        when={deleteConfirmation.show}
        title="Delete Repository"
        description={`Are you sure you want to delete the repository "${deleteConfirmation.repositoryName}"? This action cannot be undone.`}
        confirm={confirmDeleteRepository}
        cancel={cancelDeleteRepository}
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default Repositories;
